import { useAuthContext } from '@/auth/useAuthContext';
import { buildSeenOnceKey, readSeen, writeSeen } from '@/components/spotlight-tour/useSeenOnce';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import AnnouncementModal from './AnnouncementModal';
import { announcementShape } from './announcementShape';
import { ANNOUNCEMENT_STATUS, setAnnouncementStatus } from './announcementStatus';
import isAnnouncementExpired from './isAnnouncementExpired';

const seenKey = (id, userId) => buildSeenOnceKey(`announcement:${id}`, userId);

Announcements.propTypes = {
  announcements: PropTypes.oneOfType([announcementShape, PropTypes.arrayOf(announcementShape)])
    .isRequired,
};

export default function Announcements({ announcements }) {
  const { user } = useAuthContext();
  const userId = user?.id;
  const [dismissed, setDismissed] = useState(false);

  const active = useMemo(() => {
    const list = Array.isArray(announcements) ? announcements : [announcements];
    const now = new Date();
    return list.filter(
      (announcement) => announcement && !isAnnouncementExpired(announcement.expiresAt, now)
    );
  }, [announcements]);

  // Lido no render, e não num efeito como no useSeenOnce: o primeiro render com usuário já sabe se
  // o modal abre, sem um render `idle` no meio que deixaria o tour de Produtos passar na frente.
  const unseen = useMemo(
    () => (userId ? active.filter(({ id }) => !readSeen(seenKey(id, userId))) : []),
    [active, userId]
  );

  const isOpen = !dismissed && unseen.length > 0;

  let status = ANNOUNCEMENT_STATUS.IDLE;
  if (isOpen) {
    status = ANNOUNCEMENT_STATUS.OPEN;
  } else if (!userId && active.length > 0) {
    status = ANNOUNCEMENT_STATUS.PENDING;
  }

  useEffect(() => {
    setAnnouncementStatus(status);
  }, [status]);

  useEffect(() => () => setAnnouncementStatus(ANNOUNCEMENT_STATUS.IDLE), []);

  const handleClose = () => {
    unseen.forEach(({ id }) => writeSeen(seenKey(id, userId)));
    setDismissed(true);
  };

  if (!isOpen) return null;

  return <AnnouncementModal announcements={unseen} onClose={handleClose} />;
}
